import type { EmotionScore } from "@shared/hubs";

// One colour per emotion, used wherever the small bars appear.
export const EMOTION_BAR_COLORS: Record<string, string> = {
  Sadness: "#3b6ea8",
  Joy: "#e0a526",
  Tension: "#db2777",
  Anger: "#b91c1c",
  Nostalgia: "#a16207",
  Wonder: "#7c3aed",
  Interest: "#0e7490",
};

// The three emotions that stand out for a work, as thin labelled bars (0-100).
export default function EmotionMiniBars({ emotions, className = "" }: { emotions?: EmotionScore[]; className?: string }) {
  if (!emotions?.length) return null;
  return (
    <div
      className={`space-y-0.5 ${className}`}
      aria-label={`Stands out for ${emotions.map(([k, v]) => `${k} ${v}`).join(", ")}`}
    >
      {emotions.map(([name, value]) => (
        <div key={name} className="flex items-center gap-1.5 text-[11px] leading-tight text-muted-foreground">
          <span className="w-16 shrink-0 truncate">{name}</span>
          <span className="relative h-1.5 flex-1 rounded-full bg-gray-100 overflow-hidden">
            <span
              className="absolute inset-y-0 left-0 rounded-full"
              style={{ width: `${value}%`, backgroundColor: EMOTION_BAR_COLORS[name] ?? "#6b7280" }}
            />
          </span>
          <span className="w-6 shrink-0 text-right tabular-nums">{value}</span>
        </div>
      ))}
    </div>
  );
}

// A hub's average score on each emotion against the whole collection.
export function EmotionProfile({ profile }: { profile: { emotion: string; hub: number; all: number }[] }) {
  if (!profile.length) return null;
  return (
    <div className="space-y-1.5 max-w-xl">
      {profile.map((p) => (
        <div key={p.emotion} className="flex items-center gap-2 text-sm">
          <span className="w-20 shrink-0">{p.emotion}</span>
          <span className="relative h-3 flex-1 rounded bg-gray-100">
            <span
              className="absolute inset-y-0 left-0 rounded"
              style={{ width: `${p.hub}%`, backgroundColor: EMOTION_BAR_COLORS[p.emotion] ?? "#6b7280" }}
            />
            <span
              className="absolute -inset-y-0.5 w-0.5 bg-gray-800"
              style={{ left: `${p.all}%` }}
              title={`Collection average ${p.all}`}
            />
          </span>
          <span className="w-16 shrink-0 text-right tabular-nums text-muted-foreground">
            {p.hub} <span className="text-xs">({p.hub >= p.all ? "+" : ""}{p.hub - p.all})</span>
          </span>
        </div>
      ))}
      <p className="text-xs text-muted-foreground pt-1">Bars show the average score (0–100); the dark mark is the collection average.</p>
    </div>
  );
}
